// =============================================================================
// PAGES/ADMIN-EXTENSOES-TOKENS.TSX — Admin › Extensões / Tokens
// =============================================================================
// Tela dedicada do admin para administrar os tokens das extensões (padrão
// hub-laravel-extensoes-e-tokens §7): tabela de usuários × status do token
// (Sem token / Aguardando o usuário / Ativo; Conectada / Nunca conectou), com
// Gerar / Gerar novo / Revogar. O admin NUNCA vê o token — o usuário revela 1x.
// =============================================================================
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, KeyRound, RefreshCw, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { extensaoApi, type AdminTokenLinha } from '../services/api';

function fmtData(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
}

const BADGE: Record<AdminTokenLinha['estado'], { txt: string; cls: string }> = {
  sem_token: { txt: 'Sem token', cls: 'bg-otm-subtle text-otm-txt-3' },
  aguardando: { txt: 'Aguardando o usuário', cls: 'bg-amber-100 text-amber-800' },
  ativo: { txt: 'Ativo', cls: 'bg-green-100 text-green-700' },
};

export default function AdminExtensoesTokens() {
  const navigate = useNavigate();
  const [linhas, setLinhas] = useState<AdminTokenLinha[] | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [acao, setAcao] = useState<string | null>(null);
  const [busca, setBusca] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    try { setLinhas(await extensaoApi.listarTokens()); }
    catch { toast.error('Falha ao carregar os tokens.'); }
    finally { setCarregando(false); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  async function gerar(l: AdminTokenLinha) {
    if (l.estado !== 'sem_token' && !confirm(`Gerar um novo token para ${l.nome} revoga o atual — a extensão dele(a) precisará ser reconfigurada. Continuar?`)) return;
    setAcao(l.usuarioId);
    try { await extensaoApi.gerarToken(l.usuarioId); toast.success(`Token gerado para ${l.nome}. O usuário revela em Extensões.`); carregar(); }
    catch { toast.error('Falha ao gerar.'); } finally { setAcao(null); }
  }
  async function revogar(l: AdminTokenLinha) {
    if (!confirm(`Revogar o token de ${l.nome} desconecta a extensão dele(a). Continuar?`)) return;
    setAcao(l.usuarioId);
    try { await extensaoApi.revogarToken(l.usuarioId); toast.success('Token revogado.'); carregar(); }
    catch { toast.error('Falha ao revogar.'); } finally { setAcao(null); }
  }

  const q = busca.trim().toLowerCase();
  const visiveis = (linhas ?? []).filter((l) => !q || l.nome.toLowerCase().includes(q) || l.email.toLowerCase().includes(q));

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-otm-txt-3 hover:text-otm-txt-1 mb-4"><ArrowLeft size={16} /> Voltar</button>
      <div className="flex items-center gap-2 mb-1"><KeyRound size={22} className="text-otm-orange" /><h1 className="text-xl font-bold text-otm-txt-1">Extensões / Tokens</h1></div>
      <p className="text-sm text-otm-txt-3 mb-4">Gere o token de conexão de cada operador. Você nunca vê o token — o usuário o revela uma única vez em <strong>Extensões</strong>.</p>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-otm-txt-3" />
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar usuário…" className="w-full pl-9 pr-3 py-2 rounded-lg border border-otm-border text-sm focus:outline-none focus:ring-2 focus:ring-otm-orange/40 focus:border-otm-orange" />
        </div>
        <button onClick={carregar} disabled={carregando} className="inline-flex items-center gap-1.5 text-sm text-otm-txt-3 hover:text-otm-txt-1 disabled:opacity-40" title="Recarregar"><RefreshCw size={16} className={carregando ? 'animate-spin' : ''} /></button>
      </div>

      <div className="bg-white rounded-2xl border border-otm-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-otm-txt-3 border-b border-otm-border">
                <th className="px-4 py-2.5 font-semibold">Usuário</th>
                <th className="px-4 py-2.5 font-semibold">Token</th>
                <th className="px-4 py-2.5 font-semibold">Conexão</th>
                <th className="px-4 py-2.5 font-semibold text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {carregando && !linhas ? (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-otm-txt-3"><Loader2 size={16} className="animate-spin inline" /> Carregando…</td></tr>
              ) : !visiveis.length ? (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-otm-txt-3">Nenhum usuário.</td></tr>
              ) : visiveis.map((l) => (
                <tr key={l.usuarioId} className="border-b border-otm-border/60 last:border-0">
                  <td className="px-4 py-2.5"><div className="font-medium text-otm-txt-1">{l.nome}</div><div className="text-xs text-otm-txt-3">{l.email}</div></td>
                  <td className="px-4 py-2.5"><span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${BADGE[l.estado].cls}`}>{BADGE[l.estado].txt}</span>{l.geradoEm && l.estado !== 'sem_token' ? <div className="text-[11px] text-otm-txt-3 mt-0.5">gerado {fmtData(l.geradoEm)}</div> : null}</td>
                  <td className="px-4 py-2.5 text-xs text-otm-txt-2">
                    {l.estado === 'sem_token' ? '—'
                      : l.conectada ? <span className="text-green-700 font-medium">Conectada{l.ultimaVersao ? ` · v${l.ultimaVersao}` : ''}{l.versaoDesatualizada ? ' (desatualizada)' : ''}</span>
                      : l.nuncaConectou ? 'Nunca conectou'
                      : `Desconectada · ${fmtData(l.ultimaConexaoEm)}`}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5 justify-end">
                      <button onClick={() => gerar(l)} disabled={acao === l.usuarioId} className="rounded-lg border border-otm-border px-2.5 py-1.5 text-xs text-otm-txt-1 hover:bg-otm-subtle disabled:opacity-40">{l.estado === 'sem_token' ? 'Gerar' : 'Gerar novo'}</button>
                      {l.estado !== 'sem_token' && <button onClick={() => revogar(l)} disabled={acao === l.usuarioId} className="rounded-lg border border-red-200 text-red-700 px-2.5 py-1.5 text-xs hover:bg-red-50 disabled:opacity-40">Revogar</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
